import React, { useState } from 'react';
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const GREEN = '#097049';
const DARK_GREEN = '#0A3F2B';
const CREAM = '#F6F1EA';
const INK = '#172033';
const MUTED = '#6B7280';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim()) {
      setError('Email wajib diisi.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (res.ok) {
        setSent(true);
      } else {
        setError(data.detail || 'Terjadi kesalahan, coba lagi.');
      }
    } catch {
      setError('Gagal terhubung ke server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stockara-auth-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box}
        body{margin:0}
        .stockara-auth-page{
          position:relative;
          overflow:hidden;
          min-height:100vh;
          --sa-display-font:"Instrument Serif", Georgia, "Times New Roman", serif;
          --sa-body-font:"Plus Jakarta Sans", Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-family:var(--sa-body-font);
          color:${INK};
          background:
            linear-gradient(rgba(9,112,73,.075) 1px, transparent 1px),
            linear-gradient(90deg, rgba(9,112,73,.075) 1px, transparent 1px),
            #fbfaf7;
          background-size:28px 28px,28px 28px,auto;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          padding:32px 18px;
        }
        .stockara-auth-page::before{
          content:'';
          position:absolute;
          inset:-24%;
          z-index:0;
          pointer-events:none;
          background:
            radial-gradient(circle at 16% 18%, rgba(22,199,132,.24), transparent 30%),
            radial-gradient(circle at 82% 72%, rgba(9,112,73,.16), transparent 28%),
            radial-gradient(circle at 50% 50%, rgba(246,241,234,.86), transparent 36%);
          filter:blur(1px);
          animation:sa-auth-gradient-drift 22s ease-in-out infinite alternate;
        }
        @keyframes sa-auth-gradient-drift{
          0%{transform:translate3d(-2%, -1%, 0) scale(1)}
          50%{transform:translate3d(2%, 2%, 0) scale(1.04)}
          100%{transform:translate3d(-1%, 3%, 0) scale(1.02)}
        }
        .sa-auth-shell{
          position:relative;
          z-index:1;
          width:min(100%, 980px);
          min-height:640px;
          display:grid;
          grid-template-columns:minmax(0,.93fr) minmax(0,1.07fr);
          gap:42px;
          padding:14px;
          border:1px solid rgba(15,23,42,.10);
          border-radius:22px;
          background:rgba(255,255,255,.88);
          box-shadow:0 26px 80px rgba(15,23,42,.12);
          backdrop-filter:blur(14px);
        }
        .sa-auth-brand{
          position:relative;
          overflow:hidden;
          min-height:612px;
          border-radius:16px;
          padding:42px;
          display:flex;
          flex-direction:column;
          justify-content:flex-end;
          background:
            linear-gradient(rgba(9,112,73,.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(9,112,73,.07) 1px, transparent 1px),
            ${CREAM};
          background-size:18px 18px,18px 18px,auto;
        }
        .sa-auth-brand::before{
          content:'';
          position:absolute;
          inset:-24%;
          pointer-events:none;
          background:
            radial-gradient(circle at 78% 16%, rgba(42,238,141,.44), transparent 34%),
            radial-gradient(circle at 22% 72%, rgba(9,112,73,.16), transparent 28%),
            radial-gradient(circle at 52% 42%, rgba(255,250,244,.70), transparent 36%);
          animation:sa-auth-gradient-drift 18s ease-in-out infinite alternate;
        }
        .sa-auth-brand > *{
          position:relative;
          z-index:1;
        }
        .sa-brand-logo{
          display:flex;
          align-items:center;
          gap:10px;
          color:${GREEN};
          font-weight:900;
          font-size:22px;
          margin-bottom:28px;
        }
        .sa-brand-logo img{width:32px;height:32px;border-radius:9px;object-fit:contain}
        .sa-auth-brand h1{
          width:min(100%,430px);
          margin:0;
          color:#202020;
          font-family:var(--sa-display-font);
          font-size:clamp(40px,4.8vw,58px);
          line-height:0.98;
          font-weight:400;
        }
        .sa-auth-brand h1 span{
          color:${GREEN};
          text-decoration:underline;
          text-decoration-thickness:4px;
          text-underline-offset:7px;
        }
        .sa-auth-panel{
          display:flex;
          align-items:center;
          justify-content:center;
          padding:42px 30px;
        }
        .sa-auth-inner{width:min(100%,440px)}
        .sa-back-link{
          display:inline-flex;
          align-items:center;
          gap:12px;
          min-height:38px;
          color:${MUTED};
          text-decoration:none;
          font-size:14px;
          font-weight:750;
          margin-bottom:64px;
        }
        .sa-back-link span:first-child{
          width:28px;
          height:28px;
          border:1px solid rgba(15,23,42,.12);
          border-radius:10px;
          display:flex;
          align-items:center;
          justify-content:center;
          background:#fff;
          color:#64748b;
        }
        .sa-title{
          margin:0;
          color:${INK};
          font-family:var(--sa-display-font);
          font-size:34px;
          line-height:1.06;
          font-weight:400;
        }
        .sa-desc{
          margin:10px 0 52px;
          color:${MUTED};
          font-size:15px;
          line-height:1.45;
          max-width:420px;
        }
        .sa-label{
          display:block;
          margin-bottom:8px;
          color:#222;
          font-size:14px;
          font-weight:650;
        }
        .sa-input-wrap{position:relative;margin-bottom:18px}
        .sa-input-icon{
          position:absolute;
          left:13px;
          top:50%;
          transform:translateY(-50%);
          color:#94a3b8;
          display:flex;
        }
        .sa-input{
          width:100%;
          height:44px;
          border:1px solid #d7dbe0;
          border-radius:11px;
          background:#fff;
          padding:0 14px 0 42px;
          color:${INK};
          font:inherit;
          font-size:14px;
          outline:none;
          transition:border-color .16s ease, box-shadow .16s ease;
        }
        .sa-input:focus{border-color:${GREEN};box-shadow:0 0 0 3px rgba(9,112,73,.12)}
        .sa-input.is-error{border-color:#ef4444;box-shadow:0 0 0 3px rgba(239,68,68,.09)}
        .sa-alert{
          margin:0 0 16px;
          padding:11px 13px;
          border-radius:11px;
          border:1px solid #fecaca;
          background:#fef2f2;
          color:#b91c1c;
          font-size:13px;
          font-weight:650;
        }
        .sa-submit{
          width:100%;
          height:44px;
          border:0;
          border-radius:11px;
          background:linear-gradient(180deg,#1b8a5f,#074a31);
          color:#fff;
          font:inherit;
          font-size:14px;
          font-weight:850;
          cursor:pointer;
          box-shadow:0 14px 28px rgba(9,112,73,.18);
          transition:opacity .16s ease, transform .16s ease;
        }
        .sa-submit:hover{transform:translateY(-1px)}
        .sa-submit:disabled{opacity:.65;cursor:not-allowed;transform:none}
        .sa-success{
          padding:4px 0;
        }
        .sa-success-icon{
          width:54px;
          height:54px;
          border-radius:16px;
          background:#e8f7ef;
          color:${GREEN};
          display:flex;
          align-items:center;
          justify-content:center;
          margin-bottom:22px;
        }
        .sa-success-email{
          margin:0 0 22px;
          padding:12px 14px;
          border:1px solid rgba(9,112,73,.18);
          border-radius:12px;
          background:#f4fbf7;
          color:${DARK_GREEN};
          font-size:14px;
          font-weight:800;
          word-break:break-word;
        }
        .sa-secondary-link{
          width:100%;
          height:42px;
          margin-top:16px;
          border:1px solid #d7dbe0;
          border-radius:11px;
          background:#fff;
          color:#202020;
          text-decoration:none;
          display:flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          font-size:14px;
          font-weight:800;
          transition:border-color .16s ease, color .16s ease, background .16s ease;
        }
        .sa-secondary-link:hover{border-color:${GREEN};color:${GREEN};background:#f7fbf8}
        .sa-auth-terms{
          position:relative;
          z-index:1;
          margin-top:88px;
          text-align:center;
          color:#6b7280;
          font-size:13px;
          line-height:1.45;
        }
        .sa-auth-terms a{color:#2f3a44;text-decoration:underline;text-underline-offset:3px}
        @media(max-width:820px){
          .stockara-auth-page{justify-content:flex-start;padding:14px}
          .sa-auth-shell{grid-template-columns:1fr;gap:0;min-height:auto;padding:10px}
          .sa-auth-brand{min-height:250px;padding:28px}
          .sa-auth-brand h1{font-size:34px}
          .sa-auth-panel{padding:28px 16px}
          .sa-back-link{margin-bottom:34px}
          .sa-desc{margin-bottom:34px}
          .sa-auth-terms{margin-top:28px}
        }
        @media(max-width:460px){
          .sa-auth-brand{min-height:230px;padding:24px}
          .sa-auth-brand h1{font-size:31px}
          .sa-brand-logo{font-size:20px}
        }
        @media(prefers-reduced-motion:reduce){
          .stockara-auth-page::before,
          .sa-auth-brand::before{animation:none}
        }
      `}</style>

      <div className="sa-auth-shell">
        <section className="sa-auth-brand" aria-label="Stockara AI">
          <div>
            <div className="sa-brand-logo">
              <img src="/stockara-ai-icon.png" alt="" />
              <span>Stockara AI</span>
            </div>
            <h1>Reset akses tanpa ganggu <span>operasional</span> bisnis.</h1>
          </div>
        </section>

        <section className="sa-auth-panel">
          <div className="sa-auth-inner">
            <Link className="sa-back-link" to="/login">
              <span><ArrowLeft size={16} /></span>
              <span>Back to login</span>
            </Link>

            {!sent ? (
              <form onSubmit={handleSubmit}>
                <h1 className="sa-title">Lupa password?</h1>
                <p className="sa-desc">
                  Masukkan email yang terdaftar di Stockara. Kami akan mengirim link reset password ke email kamu.
                </p>

                <label className="sa-label" htmlFor="forgot-email">Email</label>
                <div className="sa-input-wrap">
                  <span className="sa-input-icon"><Mail size={17} /></span>
                  <input
                    id="forgot-email"
                    className={`sa-input ${error ? 'is-error' : ''}`}
                    type="email"
                    value={email}
                    onChange={(event) => { setEmail(event.target.value); setError(''); }}
                    placeholder="email@contoh.com"
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                {error && <p className="sa-alert">{error}</p>}

                <button className="sa-submit" type="submit" disabled={loading}>
                  {loading ? 'Mengirim...' : 'Kirim link reset'}
                </button>
              </form>
            ) : (
              <div className="sa-success">
                <div className="sa-success-icon">
                  <CheckCircle2 size={30} />
                </div>
                <h1 className="sa-title">Cek email kamu</h1>
                <p className="sa-desc">
                  Link reset password sudah dikirim. Link aktif selama 1 jam, jadi langsung cek inbox atau folder spam.
                </p>
                <p className="sa-success-email">{email}</p>
                <Link className="sa-secondary-link" to="/login">
                  <ArrowLeft size={16} />
                  Back to login
                </Link>
              </div>
            )}
          </div>
        </section>
      </div>

      <p className="sa-auth-terms">
        Dengan menggunakan Stockara, Anda menyetujui<br />
        <a href="/terms">Syarat & Ketentuan</a> dan <a href="/privacy">Kebijakan Privasi</a>
      </p>
    </div>
  );
}
